<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from 'dashboard/routes';
import uazapiChannel from 'dashboard/api/channel/uazapiChannel';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();

const STEP = { FORM: 'form', QR: 'qr', CONNECTED: 'connected' };
const step = ref(STEP.FORM);

const inboxName = ref('');
const baseUrl = ref('https://geniuscloud.uazapi.com');
const inboxId = ref(null);
const channelId = ref(null);
const qrCodeData = ref(null);
const phoneNumber = ref(null);
let pollTimer = null;

const v$ = useVuelidate(
  { inboxName: { required }, baseUrl: { required, url } },
  { inboxName, baseUrl }
);

const isCreating = ref(false);

const startPolling = () => {
  pollTimer = setInterval(async () => {
    const { data } = await uazapiChannel.status(channelId.value);
    if (data.qr_code_data) {
      qrCodeData.value = data.qr_code_data;
    }
    if (data.status === 'connected') {
      phoneNumber.value = data.phone_number;
      step.value = STEP.CONNECTED;
      clearInterval(pollTimer);
    }
  }, 4000);
};

const enterQrStep = async () => {
  step.value = STEP.QR;

  try {
    const { data } = await uazapiChannel.qrCode(channelId.value);
    if (data.qr_code_data) {
      qrCodeData.value = data.qr_code_data;
    }
  } catch {
    // The first status poll tick will pick up the QR code if this call fails.
  }

  startPolling();
};

const createChannel = async () => {
  if (isCreating.value) {
    return;
  }

  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  isCreating.value = true;
  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: { type: 'uazapi', base_url: baseUrl.value },
    });

    inboxId.value = inbox.id;
    channelId.value = inbox.channel_id;
    qrCodeData.value = inbox.qr_code_data || null;

    await enterQrStep();
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.API.ERROR_MESSAGE')
    );
  } finally {
    isCreating.value = false;
  }
};

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);
});

const goToAgents = () => {
  router.replace({
    name: 'settings_inboxes_add_agents',
    params: { page: 'new', inbox_id: inboxId.value },
  });
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      v-if="step === STEP.FORM"
      :header-title="t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.TITLE')"
      :header-content="t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.DESC')"
    />

    <form
      v-if="step === STEP.FORM"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.baseUrl.$error }">
          {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.BASE_URL.LABEL') }}
          <input
            v-model="baseUrl"
            type="text"
            :placeholder="
              t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.BASE_URL.PLACEHOLDER')
            "
            @blur="v$.baseUrl.$touch"
          />
        </label>
        <p class="help-text">
          {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.BASE_URL.SUBTITLE') }}
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="isCreating"
          :disabled="isCreating"
          type="submit"
          solid
          blue
          :label="t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>

    <div v-else-if="step === STEP.QR" class="flex flex-col items-center gap-4">
      <h3 class="text-lg font-medium">
        {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.QR.TITLE') }}
      </h3>
      <p class="text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.QR.SUBTITLE') }}
      </p>
      <img
        v-if="qrCodeData"
        :src="qrCodeData"
        alt="QR code"
        class="w-56 h-56"
      />
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.QR.WAITING') }}
      </p>
    </div>

    <div v-else class="flex flex-col items-center gap-4">
      <p>
        {{ t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.QR.CONNECTED', { phoneNumber }) }}
      </p>
      <NextButton
        solid
        blue
        :label="t('INBOX_MGMT.ADD.UAZAPI_CHANNEL.SUBMIT_BUTTON')"
        @click="goToAgents"
      />
    </div>
  </div>
</template>
